import { create } from "zustand";
import { persist } from "zustand/middleware";
import { sentryService } from "@shared/services/sentry";
import type { AuthState, User } from "./types";

const toIdentity = (user: User | null) =>
  user ? { id: String(user.id), email: user.email } : null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      setSession: (session) => {
        const { id, email, first_name, last_name } = session.user;
        set({
          user: { id, email, first_name, last_name },
          token: session.token,
          refreshToken: session.refresh_token,
          isAuthenticated: true,
        });
        sentryService.setUser({ id: String(id), email });
      },
      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
        const identity = toIdentity(user);
        if (identity) {
          sentryService.setUser(identity);
        } else {
          sentryService.clearUser();
        }
      },
      setToken: (token) => set({ token }),
      setRefreshToken: (refreshToken) => set({ refreshToken }),
      logout: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
        });
        sentryService.clearUser();
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => () => {
        useAuthStore.setState((s) => ({
          isAuthenticated: !!s.user && !!s.token,
        }));
      },
    }
  )
);
